import { isValidElement, type ComponentPropsWithoutRef } from "react";
import styled from "styled-components";
import { Icon } from "src/elements";
import type { ButtonIcon, Props as AppButtonProps } from "src/elements/Button/Button";

import { ReviewColor } from "./reviewDesignTokens";
export { ReviewColor } from "./reviewDesignTokens";

type Props = Omit<ComponentPropsWithoutRef<"button">, "color"> &
  Pick<
    AppButtonProps,
    | "size"
    | "variant"
    | "color"
    | "iconLeft"
    | "iconRight"
    | "isDisabled"
    | "isEnabled"
    | "isLoading"
    | "ariaLabel"
    | "ariaPressed"
    | "ariaExpanded"
    | "ariaBusy"
    | "width"
    | "height"
  >;

const Control = styled.button<{
  $size?: Props["size"];
  $variant?: Props["variant"];
  $danger: boolean;
  $width?: string;
  $height?: string;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  flex-shrink: 0;
  min-height: ${({ $size }) => ($size === "small" ? "34px" : "38px")};
  width: ${({ $width }) => $width};
  height: ${({ $height }) => $height};
  padding: 7px 12px;
  border: 1px solid
    ${({ $variant }) => ($variant === "borderless" ? "transparent" : ReviewColor.line)};
  border-radius: 8px;
  background: ${({ $variant, $danger }) =>
    !$variant || $variant === "default"
      ? $danger
        ? ReviewColor.dangerBg
        : ReviewColor.accent
      : "transparent"};
  color: ${({ $variant, $danger }) =>
    $danger ? ReviewColor.danger : !$variant || $variant === "default" ? "#fff" : ReviewColor.ink};
  font: inherit;
  font-size: 13px;
  line-height: 1.3;
  font-weight: 600;
  text-transform: none;
  letter-spacing: normal;
  cursor: pointer;
  transition:
    background 120ms ease,
    box-shadow 120ms ease;
  &:hover:not(:disabled) {
    box-shadow: inset 0 0 0 999px #172b330a;
  }
  &:focus-visible {
    outline: 3px solid ${ReviewColor.accent};
    outline-offset: 2px;
  }
  &[aria-busy="true"]::before {
    content: "";
    width: 12px;
    height: 12px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: review-spin 800ms linear infinite;
  }
  @keyframes review-spin {
    to {
      transform: rotate(360deg);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    &[aria-busy="true"]::before {
      animation: none;
    }
  }
  &:disabled {
    cursor: default;
    opacity: 0.45;
  }
`;

const renderIcon = (icon: ButtonIcon | undefined) => {
  if (isValidElement(icon)) return icon;
  if (icon && "name" in icon)
    return <Icon name={icon.name} size={icon.size ?? 14} color="currentColor" />;
  return null;
};

/** Review-only controls: readable labels without changing buttons elsewhere in the app. */
export const ReviewButton = ({
  size,
  variant,
  color,
  iconLeft,
  iconRight,
  isDisabled,
  isEnabled,
  isLoading,
  ariaLabel,
  ariaPressed,
  ariaExpanded,
  ariaBusy,
  width,
  height,
  children,
  type = "button",
  ...rest
}: Props) => (
  <Control
    {...rest}
    type={type}
    $size={size}
    $variant={variant}
    $danger={color === "red"}
    $width={width}
    $height={height}
    disabled={rest.disabled || isDisabled || isEnabled === false || isLoading}
    aria-label={ariaLabel ?? rest["aria-label"]}
    aria-pressed={ariaPressed ?? rest["aria-pressed"]}
    aria-expanded={ariaExpanded ?? rest["aria-expanded"]}
    aria-busy={isLoading || ariaBusy || rest["aria-busy"]}
  >
    {renderIcon(iconLeft)}
    {children}
    {renderIcon(iconRight)}
  </Control>
);
